import { LanguageSwitcher } from '@components/shared/LanguageSwitcher';
import { themeColor } from '@config/theme';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useTruncate } from '@hooks/useTruncate';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { WindowSize } from '../types';

interface HeaderProps {
	user: any;
	size: WindowSize;
	isFarmerPath: boolean;
	isChat: boolean;
	showPrimaryDrawer: boolean;
	pageTitle: string;
	handleDrawerToggle: () => void;
	handleClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export const Header = ({
	user,
	size,
	isFarmerPath,
	isChat,
	showPrimaryDrawer,
	pageTitle,
	handleDrawerToggle,
	handleClick,
}: HeaderProps) => {
	const { t } = useTranslation();
	const { truncate } = useTruncate();
	const { navigateWithLanguage } = useLanguageNavigate();
	const showGreeting = t('hi') !== 'hi'; // Simple check if greeting is translated

	return (
		<AppBar
			position="sticky"
			className={classNames('header', {
				[isChat ? '!shadow-none' : '']: true,
				[isChat && size.width > 997 ? 'py-[14px] pt-[16px] pl-[30px]' : '']: true,
				[isChat && size.width > 576 ? '!border-b-[1px] !border-solid !themeColor.offWhite' : '']:
					true,
			})}
			style={{ backgroundColor: isChat ? themeColor.cream : '' }}
		>
			<Toolbar>
				{size.width <= 576 ? (
					<div className="dashboard-header flex flex-col justify-between">
						<div className="flex justify-between items-center gap-[4px] w-[100%]">
							<div className="flex justify-center items-center">
								{!showPrimaryDrawer && (
									<IconButton
										color="primary"
										aria-label="open drawer"
										edge="start"
										onClick={handleDrawerToggle}
									>
										<MenuIcon />
									</IconButton>
								)}
								{size.width >= 600 && (
									<div className="dashboard-user">
										{showGreeting && t('hi')}{' '}
										{size.width >= 768 && size.width < 1200 ? (
											<Tooltip title={user?.first_name || ''} arrow>
												<span>{truncate(user?.first_name || '', 20)}</span>
											</Tooltip>
										) : (
											<span>{user?.first_name || ''}</span>
										)}
									</div>
								)}
							</div>
							<div className="flex justify-center items-center gap-[15px]">
								{isFarmerPath ? (
									<>
										<Button
											type="submit"
											variant="outlined"
											sx={{
												padding: '8px 10px',
												fontWeight: '700',
												fontSize: '12px',
											}}
											onClick={handleClick}
										>
											<AccountCircleIcon className="mr-[10px]" />
											{t('farmer')}
										</Button>
										<Button
											type="submit"
											variant="outlined"
											onClick={() => navigateWithLanguage('/farmer/services')}
											sx={{
												padding: '8px 10px',
												fontWeight: '700',
												fontSize: '12px',
												'@media (max-width: 992px)': {
													display: 'none',
												},
											}}
										>
											<PersonSearchOutlinedIcon className="mr-[10px]" />
											{t('findSpecialist')}
										</Button>
									</>
								) : (
									<Button
										type="submit"
										variant="outlined"
										sx={{
											padding: '8px 10px',
											fontWeight: '700',
											fontSize: '12px',
										}}
										onClick={handleClick}
									>
										<AccountCircleOutlinedIcon className="mr-[10px]" />
										{user?.is_company ? t('company') : t('specialists')}
									</Button>
								)}
								<LanguageSwitcher />
							</div>
						</div>
						<div
							className={classNames('flex justify-start w-[100%] mt-[10px]', {
								hidden: isChat,
							})}
						>
							<Typography
								className="dashboard-title flex items-center"
								variant="h6"
								noWrap
								component="div"
							>
								<KeyboardArrowRightIcon className="text-primary" />
								{pageTitle}
							</Typography>
						</div>
					</div>
				) : (
					<>
						{!showPrimaryDrawer && (
							<IconButton
								color="primary"
								aria-label="open drawer"
								edge="start"
								onClick={handleDrawerToggle}
							>
								<MenuIcon />
							</IconButton>
						)}
						<div
							className={classNames(
								'dashboard-header ml-[320px]',
								size.width <= 1200 ? '!ml-[120px]' : '',
								'max-lg:!ml-0'
							)}
						>
							<Typography
								className={classNames('dashboard-title flex items-center', {
									hidden: isChat && size.width < 993,
								})}
								variant="h6"
								noWrap
								component="div"
							>
								<KeyboardArrowRightIcon className="text-primary" />
								{pageTitle}
							</Typography>
							<Box
								className={classNames('', {
									hidden: isChat && size.width >= 1200,
								})}
							></Box>
							<div className="flex justify-center items-center gap-[12px]">
								{size.width >= 600 && (
									<div className="dashboard-user">
										{t('hi')}{' '}
										{size.width >= 768 && size.width < 1200 ? (
											<Tooltip title={user?.first_name || ''} arrow>
												<span>{truncate(user?.first_name || '', 20)}</span>
											</Tooltip>
										) : (
											<span>{user?.first_name || ''}</span>
										)}
									</div>
								)}
								{isFarmerPath ? (
									<>
										<Button
											type="submit"
											variant="outlined"
											sx={{
												padding: '9px 18px',
												fontWeight: '700',
												fontSize: '12px',
											}}
											onClick={handleClick}
										>
											<AccountCircleIcon className="mr-[10px]" />
											{t('farmer')}
										</Button>
										{size.width > 992 && <div className="divider"></div>}
										<Button
											type="submit"
											variant="outlined"
											onClick={() => navigateWithLanguage('/farmer/services')}
											sx={{
												padding: { xs: '4px', sm: '8px' },
												fontWeight: '700',
												fontSize: '12px',
												background: themeColor.darkTeal,
												color: themeColor.contrastTextWhite,
												border: 'unset',
												gap: '12px',
												'@media (max-width: 992px)': {
													display: 'none',
												},
											}}
										>
											{t('services')}
											<BusinessCenterIcon />
										</Button>
									</>
								) : (
									<Button
										type="submit"
										variant="outlined"
										sx={{
											padding: '9px 18px',
											fontWeight: '700',
											fontSize: '12px',
										}}
										onClick={handleClick}
									>
										<AccountCircleOutlinedIcon className="mr-[10px]" />
										{user?.is_company ? t('company') : t('specialists')}
									</Button>
								)}
								<LanguageSwitcher />
							</div>
						</div>
					</>
				)}
			</Toolbar>
		</AppBar>
	);
};
